import { getCause, parseAtoms, reaction, type Ctx } from '@reatom/framework'
import { reatomContext } from '@reatom/npm-react'
import React from 'react'
import ReactDOM from 'react-dom/client'

import { AsyncStateN, Redacted } from '@blade/std-lib'
import { androidBackButtonClickedAction } from '@blade/ui-kit'
import { InviteCode } from '@root/entities/referral'
import { walletsStateAtom } from '@root/entities/wallet'
import { registerUserRequestDevAction } from '@root/features/auth/authenticate'
import { logoutAction } from '@root/features/auth/logout'
import { PlatformUtil } from '@root/shared'
import { ApiError, apiErrorEvent } from '@root/shared/api'
import { AuthErrorCode } from '@root/shared/api/api-error-code'
import { browserHistory } from '@root/shared/lib/react-router'
import { initializeI18nAction, languageAtom } from '@root/shared/localization'
import { currentThemeAtom } from '@root/shared/theme/atoms/current-theme-atom'
import { delugeTheme } from '@root/shared/ui-kit'

import { App, appRenderedAtom } from './App'
import { AppProviders } from './app-providers'
import { initializeDesktopAppAction, initializeMobileAppAction } from './atoms'

export const appLoaderReaction = reaction((ctx) => {
  const appRendered = ctx.spy(appRenderedAtom)
  if (appRendered) {
    window.__APP_LOADER_HIDE__()
  }
}, 'appLoaderReaction')

const langChangeReaction = reaction((ctx) => {
  const language = ctx.spy(languageAtom)

  document.documentElement.lang = language
}, 'langChangeReaction')

const themeAppearanceChangeReaction = reaction((ctx) => {
  const { appearance } = ctx.spy(currentThemeAtom)

  document.documentElement.dataset.themeAppearance = appearance
}, 'themeAppearanceChangeReaction')

export const run = async (ctx: Ctx) => {
  if (PlatformUtil.is('tauri')) {
    window.handleAndroidBackButton = function () {
      return androidBackButtonClickedAction(ctx)
    }
  }
  addDebugGlobalFunctions(ctx)

  const isMobile = window.innerWidth < delugeTheme.mobile.width

  await (isMobile
    ? initializeMobileAppAction(ctx)
    : initializeDesktopAppAction(ctx))

  const deviceLocale = await PlatformUtil.locale()

  langChangeReaction(ctx)
  themeAppearanceChangeReaction(ctx)
  initializeI18nAction(ctx, deviceLocale || undefined)

  appLoaderReaction(ctx)

  const rootEl = document.getElementById('root')
  if (!rootEl) {
    return
  }
  const reactRoot = ReactDOM.createRoot(rootEl)
  reactRoot.render(
    // <React.StrictMode>
    <reatomContext.Provider value={ctx}>
      <AppProviders>
        <App />
      </AppProviders>
    </reatomContext.Provider>,
    // </React.StrictMode>,
  )
}

const addDebugGlobalFunctions = (ctx: Ctx) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ;(globalThis as any).getCause = getCause
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ;(globalThis as any).browserHistory = browserHistory
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ;(globalThis as any).__LOGOUT = () => logoutAction(ctx)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ;(globalThis as any).__THROW_API_TOKENS_EXPIRED = () => {
    apiErrorEvent(
      ctx,
      new ApiError.ApiError({
        code: AuthErrorCode.InvalidToken,
      }),
    )
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ;(globalThis as any).__REGISTER_USER = ({
    username,
    password,
    inviteCode,
  }: {
    username: string
    password: string
    inviteCode: string | undefined
  }) => {
    registerUserRequestDevAction(ctx, {
      password: Redacted.make(password),
      username,
      inviteCode: inviteCode ? InviteCode(inviteCode) : undefined,
    })
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ;(globalThis as any).__LOG_WALLETS = () => {
    const state = ctx.get(walletsStateAtom)
    if (!AsyncStateN.isSuccess(state)) {
      return
    }

    // eslint-disable-next-line no-console
    console.log(parseAtoms(ctx, state.value))
  }
}
